import React from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Paper,
  Typography,
  Chip,
  Button,
  Box,
  useTheme,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventIcon from '@mui/icons-material/Event';

function CustomerBookingTimeline({ bookings, onRebook }) {
  const theme = useTheme();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'confirmed':
        return <EventIcon />;
      case 'cancelled':
        return <CancelIcon />;
      case 'pending':
      default:
        return <HourglassEmptyIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'confirmed':
        return 'primary';
      case 'cancelled':
        return 'error';
      case 'pending':
      default:
        return 'warning';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  if (bookings.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'info.light',
          borderRadius: 2,
          color: 'info.dark',
        }}
      >
        <Typography variant="body1">No bookings found for this customer.</Typography>
      </Box>
    );
  }

  return (
    <Timeline
      sx={{
        '& .MuiTimelineItem-root:before': {
          flex: 0,
          padding: 0,
        },
      }}
    >
      {bookings.map((booking, index) => (
        <TimelineItem key={booking.id}>
          <TimelineOppositeContent
            sx={{
              flex: 0.3,
              minWidth: 90,
              py: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {formatDate(booking.booking_date)}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {booking.booking_date}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {booking.start_time}
            </Typography>
          </TimelineOppositeContent>

          <TimelineSeparator>
            <TimelineDot
              color={getStatusColor(booking.status)}
              sx={{
                boxShadow: booking.status === 'completed'
                  ? '0 0 0 4px rgba(74, 124, 89, 0.2)'
                  : 'none',
              }}
            >
              {getStatusIcon(booking.status)}
            </TimelineDot>
            {index < bookings.length - 1 && (
              <TimelineConnector
                sx={{
                  bgcolor: booking.status === 'completed'
                    ? 'success.light'
                    : 'grey.300',
                }}
              />
            )}
          </TimelineSeparator>

          <TimelineContent sx={{ py: 2, px: 2 }}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                background: booking.status === 'completed'
                  ? 'linear-gradient(145deg, #ffffff 0%, #f0fdf4 100%)'
                  : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {booking.service_name}
                </Typography>
                <Chip
                  label={booking.status}
                  color={getStatusColor(booking.status)}
                  size="small"
                  sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5 }}>
                <Box>
                  {booking.discount_amount > 0 ? (
                    <>
                      <Typography variant="h6" fontWeight={600} color="success.main">
                        £{(booking.service_price - booking.discount_amount).toFixed(2)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        £{booking.service_price}
                      </Typography>
                      <Typography variant="caption" display="block" color="secondary.main">
                        {booking.discount_code}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h6" fontWeight={600} color="primary.main">
                      £{booking.service_price}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={booking.payment_status || 'pending'}
                  color={getPaymentStatusColor(booking.payment_status)}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                />
              </Box>

              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<RepeatIcon />}
                onClick={() => onRebook(booking)}
                sx={{
                  mt: 1,
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    borderColor: theme.palette.primary.dark,
                  },
                }}
              >
                Rebook This Service
              </Button>
            </Paper>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
}

export default CustomerBookingTimeline;
